import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

// Remember the scroll position per path so browser Back/Forward can restore it.
const positions = new Map();

/**
 * Scroll management for client-side navigation:
 * - PUSH / REPLACE (clicking a link): jump to the top instantly.
 * - POP (browser Back/Forward): restore the previous scroll position. Because
 *   some pages (e.g. the home page sections) load their data asynchronously and
 *   grow in height after mount, we retry the restore for a short window until
 *   the document is tall enough to honour the saved offset.
 */
export default function ScrollToTop() {
  const { pathname } = useLocation();
  const navType = useNavigationType();

  // Take over scroll restoration so only our per-path logic runs.
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Continuously record the current path's scroll position.
  useEffect(() => {
    const onScroll = () => {
      positions.set(pathname, window.scrollY);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [pathname]);

  useEffect(() => {
    if (navType === 'POP') {
      const target = positions.get(pathname) ?? 0;
      if (target === 0) {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        return undefined;
      }
      let frame;
      let attempts = 0;
      const restore = () => {
        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
        window.scrollTo({ top: Math.min(target, Math.max(maxScroll, 0)), left: 0, behavior: 'instant' });
        attempts += 1;
        // Keep trying (~1s) until the page is tall enough to reach the target.
        if (maxScroll < target && attempts < 60) {
          frame = requestAnimationFrame(restore);
        }
      };
      frame = requestAnimationFrame(restore);
      return () => cancelAnimationFrame(frame);
    }

    // PUSH / REPLACE → start at the top.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    return undefined;
  }, [pathname, navType]);

  return null;
}
